import React, {ReactElement, ReactNode, useCallback} from 'react'
import {FormSection} from "../common/components/SectionedForm";
import {api} from "../common/api";
import {useSelector} from "react-redux";
import {selectedSiteSelector} from "../authentication/redux";
import {useTranslation} from "react-i18next";
import PaperStack from "../common/form/PaperStack";
import FormGroup from "../common/form/FormGroup";
import {LabelValues, LabelValueType} from "../common/components/LabelValues";
import {AnswerGroupSupervisor} from "../app/types";
import {formatUser} from "../common/util/formatters";
import {Paper, Table, TableContainer} from "@mui/material";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {UserLink} from "../users/UserLink";
import {useFormSubmissionButtons} from "../common/form/useFormSubmissmionButtons";
import {useFetchData} from "../common/servercall/useFetchData";
import {SupervisorAccount} from "@mui/icons-material";
import {SupervisorsForm, SupervisorsFormType} from "./supervisorForm";

export const useSupervisorSection = (answerGroupId: string, enabled: boolean) : FormSection<AnswerGroupSupervisor[]> => {
        const { t } = useTranslation()
        const site = useSelector(selectedSiteSelector)
        const {buildFormButtons} = useFormSubmissionButtons(false)

        const fetchSupervisors = useCallback(
            async (answerGroupId: string): Promise<AnswerGroupSupervisor[] | undefined> => {
                if (!site || !enabled) return
                const { data } = await api.get<AnswerGroupSupervisor[]>(`/api/site/${site.productId}/answergroup/${answerGroupId}/supervisors`)
                return data
            },
            [site, enabled]
        )

        const { data, isFetching, error, refetch } = useFetchData<AnswerGroupSupervisor[]>(useCallback(() => fetchSupervisors(answerGroupId), [fetchSupervisors, answerGroupId]))


        const supervisors = useCallback((data: AnswerGroupSupervisor[]) : LabelValueType[] =>
            [
                {
                    label: '',
                    value: (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 300 }} size={'small'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: 700 }}>{t('flows.table.name')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data?.map(it =>
                                        <TableRow key={it.uuid} sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                            <TableCell component="th" scope="row">
                                                <UserLink uuid={it.uuid} siteProductId={it.siteProductId} formattedUser={formatUser(it)}/>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ),
                    type: 'FULL_LINE',
                }] , [t])

        const renderSupervisors = useCallback(
            (value: AnswerGroupSupervisor[]): ReactNode => {
                return (
                    <PaperStack>
                        <FormGroup key={1} label={t('general.general')} fullWidth={true}>
                            <LabelValues labelValues={supervisors(value)} />
                        </FormGroup>
                        <div></div>
                    </PaperStack>
                )
            },
            [t, supervisors]
        )

        const onSubmit = useCallback((onSucceeded: () => void) =>
                async (formData: SupervisorsFormType) => {
                    if (!site) return
                    await api.put(`/api/site/${site.productId}/answergroup/${answerGroupId}/supervisors`, {
                        supervisorIds: formData.supervisors.map(it => it.uuid)
                    })
                    await refetch()
                    onSucceeded();
                },
            [site, answerGroupId, refetch],
        )

        const renderForm = useCallback((supervisors: AnswerGroupSupervisor[], cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
                <SupervisorsForm supervisors={{supervisors: supervisors}} buildFormButtons={buildFormButtons(cancelEditButton)} onSubmit={onSubmit(onSucceeded)} />,
            [onSubmit, buildFormButtons])

        return {
            title: t('flows.edit.section.supervisors'),
            data: data,
            isFetching: isFetching,
            error: error,
            refetch: refetch,
            renderDetails: renderSupervisors,
            renderForm: renderForm,
            enabled: () => enabled,
            icon: () => <SupervisorAccount />
        }
    }